// Hücre editörü popover'ı (M16 T10, mockup 02; T18 sekmeler — mockup 19): Değer / Kanıt / Geçmiş.
// Değer: giriş (TR serbest, parseCell normalize), durum segmenti (Dolu / XXX / Ölçülmüyor), not +
// rapor dipnotu; Enter kaydet, Esc iptal. Geçersiz giriş kırmızı kenarlık + kaydedilmez; birim
// uyuşmazlığı uyarı (engellemez) — kural 1. Kanıt: EvidencePanel (bağ değişince grid yenilenir).
// Geçmiş: ayrı endpoint yok — hücredeki updatedAt/updatedBy/updatedByKind/version/source alanları.
// Portal (`isClient`, P3-16/17): her girişte "Danışmanınıza not" (K10); açıklama istenen hücrede "Yanıtınız" etiketi (clientNote → PUT),
// Geçmiş'te iç not satırı yok + clientNote etiketi "Notunuz" / "Danışmanınızın açıklama isteği".
// TODO-KPI-06: ≤1024 px'te genişlik `min(380|460px, 100vw − 16px)` — viewport'a sığar (dikey
// sığma zaten `usePopoverPlacement` pinned modu).
// TODO-KPI-10: şirket yöneticisi (`adminClientIds ∋ clientId`) `submitted` hücrede "Şirket adına
// onayla" — `client_approved`; danışman onayı yine gerekir.
// `initialRaw` (ilk tuş, Codex #2); DR9 amber satır KpiCellUnitChangedNotice'ta (600 satır sınırı).
// Kırılım (spec §6.2): `breakdown` → KpiCellBreakdownSection (ham üst / üye Değer sekmesinde,
// türetilmiş toplam Kanıt sekmesinde raporlanan değer bloğunun üstünde); toplamda "Form yanıtını
// al" pasif + gerekçe; üye kaydı sürerken başlık `aria-busy`.

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { KpiFormMismatch, KpiResolvedCell, KpiResolvedIndicator } from "@mahir/shared";
import { normalizeCellInput, type CellInput } from "../../lib/kpi-grid-nav";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import { kpiOutlierText } from "../../lib/kpi-outlier";
import { clientApprovePortalValue } from "../../api/portal-kpi";
import { type GridNotes } from "../../lib/kpi-notes-ui";
import type { PortalUnitChanged } from "../../lib/kpi-unit-changed";
import { KPI_COPY } from "../../lib/kpi-ui";
import { useMediaQuery } from "../../lib/use-media-query";
import { useAuthStore } from "../../stores/auth-store";
import { OutlierNotice } from "../form-fill/OutlierNotice";
import { EvidencePanel } from "./EvidencePanel";
import { KpiCellClientNote } from "./KpiCellClientNote";
import { KpiCellEditorActions } from "./KpiCellEditorActions";
import { KpiCellEditorFormLink } from "./KpiCellEditorFormLink";
import { KpiCellEditorHeader } from "./KpiCellEditorHeader";
import { KpiCellHistory } from "./KpiCellHistory";
import { KpiCellNotes } from "./KpiCellNotes";
import { initialEditorRaw, KpiCellUnitChangedNotice } from "./KpiCellUnitChangedNotice";
import { KpiCellBreakdownSection, type KpiCellBreakdownProps } from "./KpiCellBreakdownSection";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { cellInputStyle as inp } from "./kpi-form-styles";
import { ReportedValueBlock } from "./ReportedValueBlock";
import { placementStyle, usePopoverPlacement } from "./use-popover-placement";

export type CellEditorTab = "value" | "evidence" | "history";

export interface KpiCellEditorProps {
  indicator: KpiResolvedIndicator;
  cell: KpiResolvedCell | undefined;
  columnLabel: string;
  /** çapa hücre (viewport konumu her ölçümde okunur — kaydırmada popover takip eder) */
  getAnchor: () => HTMLElement | null;
  onSave: (
    input: CellInput & {
      note?: string;
      footnote?: string;
      clientNote?: string;
      /** M17 — onaylı değeri değiştirirken neden (yalnız iç aktör) */
      changeReason?: string;
    },
  ) => void;
  onCancel: () => void;
  /** T18 — Kanıt sekmesi bağlamı */
  evidence: {
    clientId: string;
    indicators: KpiResolvedIndicator[];
    years: number[];
    entityId: string;
    year: number;
    readOnly: boolean;
    onChanged: () => void;
    /** T19 — portal: kanıt `api/portal-kpi.ts` üzerinden (presign set-bazlı, indirme politikası C2) */
    portalSetId?: string | undefined;
  };
  initialTab?: CellEditorTab;
  /**
   * T19 — müşteri modu: iç not alanı YOK (sunucu zaten düşürür), açıklama notu (clientNote)
   * görünür, onaylı değeri değiştirirken "yeniden onaya gider" uyarısı (DR8).
   */
  isClient?: boolean;
  /** türetilmiş (ƒx) hücrede Değer sekmesi yok — yalnız Kanıt / Geçmiş */
  valueTab?: boolean;
  /**
   * güvenlik/UX taraması 2026-10-08 (Codex #2): odaklı hücrede yazılan ilk karakter — editör bu
   * değerle açılır (ilk tuş kaybolmaz); imleç sona konur (seçili değil). XXX hücresinde yazılan
   * karakter durumu Dolu'ya çevirir (onChange ile aynı), Ölçülmüyor'da metin olarak kalır.
   */
  initialRaw?: string;
  /** Finar 14.4 — bu hücreye bağlı form yanıtı (iç aktör); "Form yanıtını al" eylemi (onaylı). */
  formLink?: KpiFormMismatch | undefined;
  /**
   * Dalga 3 — hücre notu iş parçacığı ("Notlar" bölümü, spec §6). Müşteride yazma alanı K10 "Danışmanınıza
   * not" alanıdır (Kaydet ile iş parçacığına yazılır) — bölüm yalnız okur; danışmanda "Yanıtla" alanı var.
   */
  notes?: GridNotes | undefined;
  /** Müşteride Notlar bölümündeki "Not ekle": düzenleyiciyi kapatıp iş parçacığı penceresini açar */
  onOpenNotes?: (() => void) | undefined;
  /** K6 — aynı işletmenin önceki yıl hücresi (grid'in gizli min-1 yılı dahil); 10× dışı uyarı */
  priorCell?: KpiResolvedCell | undefined;
  /** DR9 — portal: 409 KPI_UNIT_CHANGED hücre durumları (anahtar metricId|entityId|year) */
  unitChanged?: Record<string, PortalUnitChanged> | undefined;
  /** Kırılım bölümü (`breakdownEditorInfo`); yoksa davranış byte-eşit */
  breakdown?: KpiCellBreakdownProps | undefined;
}

type Mode = "filled" | "pending_xxx" | "not_measured";

export function KpiCellEditor(props: KpiCellEditorProps): JSX.Element {
  const { indicator, cell, columnLabel, getAnchor, onSave, onCancel, evidence, initialTab } = props;
  const valueTab = props.valueTab ?? true;
  const isClient = props.isClient ?? false;
  const typedFirst = props.initialRaw !== undefined && props.initialRaw !== "";
  // DR9 — bu hücrenin birim değişti durumu (portal): taslak + amber satır
  const unitChanged = isClient
    ? props.unitChanged?.[`${indicator.metricId}|${evidence.entityId}|${evidence.year}`]
    : undefined;
  const [tab, setTab] = useState<CellEditorTab>(initialTab ?? (valueTab ? "value" : "evidence"));
  const initialMode: Mode =
    cell?.status === "pending_xxx"
      ? typedFirst || !!unitChanged?.draft
        ? "filled"
        : "pending_xxx"
      : cell?.status === "not_measured"
        ? "not_measured"
        : "filled";
  const [mode, setMode] = useState<Mode>(initialMode);
  const [raw, setRaw] = useState<string>(() =>
    initialEditorRaw({ initialRaw: props.initialRaw, unitChanged, cell, indicator }),
  );
  const [note, setNote] = useState(cell?.note ?? "");
  const [footnote, setFootnote] = useState(cell?.footnote ?? "");
  const [reply, setReply] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const changeReasonId = useId();
  // M17 — neden alanı yalnız iç aktörde ve onaylı (ya da şirket onaylı / kilitli) değerde
  const askReason =
    !isClient &&
    !!cell?.valueId &&
    ["verified", "client_approved", "locked"].includes(cell.workflowState ?? "");
  const clarifying = isClient && cell?.workflowState === "clarification";
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // K6 — bloklamaz: kaydı engellemez, yalnız Değer sekmesinde amber satır.
  const outlierText = kpiOutlierText({
    raw,
    mode,
    priorCell: props.priorCell,
    indicator,
    isClient,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);
  const adminClientIds = useAuthStore((s) => s.user?.adminClientIds);
  // TODO-KPI-10 — şirket adına onay: yönetici + onay bekleyen (submitted) müşteri girdisi
  const canClientApprove =
    isClient &&
    valueTab &&
    cell?.workflowState === "submitted" &&
    !!cell.valueId &&
    !!evidence.portalSetId &&
    (adminClientIds ?? []).includes(evidence.clientId);
  const [approving, setApproving] = useState(false);

  async function approveForCompany(): Promise<void> {
    if (!cell?.valueId || !evidence.portalSetId) return;
    setApproving(true);
    setError(null);
    try {
      await clientApprovePortalValue(cell.valueId, evidence.portalSetId, cell.version ?? 0);
      evidence.onChanged();
      onCancel();
    } catch (e) {
      setError(e instanceof Error ? e.message : KPI_COPY.portal.approveForCompanyFailed);
    } finally {
      setApproving(false);
    }
  }
  // P3-17: viewport altına taşarsa hücrenin üstüne açılır; sekme değişince yeniden ölçülür.
  // Portal + fixed (2026-10-07): tablo kabının overflow'u popover'ı kırpamaz.
  const rootRef = useRef<HTMLDivElement>(null);
  const pos = usePopoverPlacement(rootRef, getAnchor, [tab, error, warning]);
  const tablet = useMediaQuery("(max-width: 1024px)");
  const baseWidth = tab === "value" ? 380 : 460;

  useEffect(() => {
    if (tab !== "value") return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    // yazarak açıldıysa karakter seçili olmasın (ikinci tuş silmesin) — imleç sona
    if (typedFirst) el.setSelectionRange(el.value.length, el.value.length);
    else el.select();
  }, [tab, typedFirst]);

  function submit(): void {
    let input: CellInput;
    if (mode === "pending_xxx") input = { status: "pending_xxx" };
    else if (mode === "not_measured") {
      input = { status: "not_measured", textValue: raw.trim() || KPI_COPY.cell.notMeasured };
    } else {
      const n = normalizeCellInput(raw, { unit: indicator.unit, valueType: indicator.valueType });
      if (n.input.status === "not_measured") {
        setError("Sayı girin ya da durumu 'Ölçülmüyor' yapın");
        return;
      }
      input = n.input;
      // Birim soneki uyuşmuyorsa ("12 GJ" kWh hücresine) ilk Enter KAYDETMEZ: uyarı editörde
      // kalır, "Yine de kaydet" ikinci basışta yazar (2026-10-08 manuel test 4.11 — eskiden
      // uyarı set edilip editör aynı anda kapandığı için hiç görünmüyordu).
      if (n.unitWarning && warning !== n.unitWarning) {
        setWarning(n.unitWarning);
        return;
      }
    }
    onSave({
      ...input,
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(footnote.trim() ? { footnote: footnote.trim() } : {}),
      ...(isClient && reply.trim() ? { clientNote: reply.trim() } : {}),
      ...(askReason && changeReason.trim() ? { changeReason: changeReason.trim() } : {}),
    });
  }

  const seg = (m: Mode, label: string): JSX.Element => (
    <button
      type="button"
      aria-pressed={mode === m}
      onClick={() => {
        setMode(m);
        setError(null);
        // XXX / Ölçülmüyor'a geçince alandaki sayı kaydedilmeyecek — kullanıcı "kayboldu"
        // sanmasın diye alan hemen temizlenir (2026-10-08 manuel test bulgusu)
        if (m === "pending_xxx") setRaw("");
        if (m === "filled") inputRef.current?.focus();
      }}
      className="px-3 py-1.5 text-xs font-semibold"
      style={{
        borderRadius: 999,
        border: "1px solid var(--color-border)",
        background: mode === m ? "var(--color-primary)" : "var(--color-surface)",
        color: mode === m ? "var(--color-surface)" : "var(--color-text-2)",
        minHeight: 32,
      }}
    >
      {label}
    </button>
  );

  const evidenceCount = cell?.evidenceCount ?? 0;
  const bd = props.breakdown;
  const bdTotal = bd?.info.role === "derived_total";
  const bdSection = bd ? (
    <KpiCellBreakdownSection {...bd} client={isClient} columnLabel={columnLabel} />
  ) : null;

  return createPortal(
    <div
      ref={rootRef}
      role="dialog"
      aria-label={`${indicator.label} · ${columnLabel}`}
      className="rounded-xl p-4"
      data-placement={pos.placement}
      style={{
        ...placementStyle(pos),
        width: tablet ? `min(${baseWidth.toString()}px, calc(100vw - 16px))` : baseWidth,
        maxHeight: "min(640px, calc(100vh - 24px))",
        overflow: "auto",
        fontFamily: "var(--font-sans)",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
      onKeyDown={(e) => {
        // Kanıt sekmesindeki modal (seçici/onay) kendi Esc'ini yönetir — editöre sızmasın.
        if (e.target instanceof HTMLElement && e.target.closest('[aria-modal="true"]')) return;
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
        // Notlar bölümündeki düğmelerde Enter değeri kaydetmesin (kendi "Gönder/Yanıtla" düğmesi var)
        if (e.target instanceof HTMLElement && e.target.closest("[data-kpi-notes]")) return;
        if (tab === "value" && e.key === "Enter" && !(e.target instanceof HTMLTextAreaElement)) {
          e.preventDefault();
          e.stopPropagation();
          submit();
        } else if (e.key === "Enter") {
          e.stopPropagation();
        }
      }}
    >
      <KpiCellEditorHeader
        title={`${indicator.label} · ${columnLabel}`}
        tab={tab}
        onTab={setTab}
        valueTab={valueTab}
        evidenceCount={evidenceCount}
        busy={
          bd?.recomputing && bd.info.role !== "member"
            ? isClient
              ? KPI_BREAKDOWN_COPY.portal.recomputing
              : KPI_BREAKDOWN_COPY.recomputing
            : null
        }
      />
      {bdTotal && tab === "evidence" ? bdSection : null}
      {bdTotal && tab === "evidence" && props.formLink && !isClient ? (
        <KpiCellEditorFormLink
          mismatch={props.formLink}
          disabledReason={KPI_BREAKDOWN_COPY.formLinkDerived}
          onApplied={() => undefined}
        />
      ) : null}

      {!valueTab && !isClient && !evidence.readOnly && tab === "evidence" ? (
        <ReportedValueBlock
          indicator={indicator}
          cell={cell}
          entityId={evidence.entityId}
          year={evidence.year}
          onSaved={() => {
            evidence.onChanged();
            onCancel();
          }}
        />
      ) : null}

      {tab === "value" && valueTab ? (
        <>
          {unitChanged ? <KpiCellUnitChangedNotice {...unitChanged} /> : null}
          {clarifying && cell?.clarificationNote ? (
            <div
              role="note"
              data-testid="kpi-client-note"
              className="text-xs mb-2 px-2 py-1 rounded-lg"
              style={{
                background: "color-mix(in srgb, var(--color-error) 8%, white)",
                color: "var(--color-error)",
              }}
            >
              <b>{KPI_COPY.workflow.clarification}:</b> {cell.clarificationNote}
            </div>
          ) : null}
          {isClient && cell?.workflowState === "verified" && cell.status === "filled" ? (
            <div
              role="note"
              data-testid="kpi-verified-warn"
              className="text-xs mb-2"
              style={{ color: "var(--color-warning-text)" }}
            >
              {KPI_COPY.portal.verifiedWarn}
            </div>
          ) : null}
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: "90px 1fr", alignItems: "center" }}
          >
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              Değer
            </span>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                aria-label="Değer"
                style={{
                  ...inp,
                  borderColor: error ? "var(--color-error)" : "var(--color-border)",
                  outline: error
                    ? "2px solid color-mix(in srgb, var(--color-error) 30%, white)"
                    : undefined,
                }}
                value={raw}
                placeholder={
                  mode === "not_measured"
                    ? "Kısa açıklama (ör. 2 yılda bir ölçülüyor)"
                    : mode === "pending_xxx"
                      ? "XXX seçili — sayı yazınca Dolu'ya döner"
                      : "1.474.400"
                }
                onChange={(e) => {
                  setRaw(e.target.value);
                  setError(null);
                  setWarning(null);
                  // XXX seçiliyken sayı yazılırsa durum kendiliğinden Dolu olur (alan kilitli değil)
                  if (mode === "pending_xxx" && e.target.value.trim() !== "") setMode("filled");
                }}
              />
              {indicator.unit ? (
                <span
                  className="text-xs"
                  style={{ color: "var(--color-text-3)", whiteSpace: "nowrap" }}
                >
                  {indicator.valueType === "percent" ? "%" : indicator.unit}
                </span>
              ) : null}
            </div>
            {outlierText !== null ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <OutlierNotice text={outlierText} />
              </div>
            ) : null}
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              Durum
            </span>
            <div className="flex gap-1 flex-wrap">
              {seg("filled", "Dolu")}
              {seg("pending_xxx", KPI_COPY.cell.pendingOption)}
              {seg("not_measured", "Ölçülmüyor")}
            </div>
            {!isClient ? (
              <>
                <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                  Not
                </span>
                <input
                  aria-label="Not (ekip içi)"
                  style={inp}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Ekip içi not"
                />
              </>
            ) : null}
            {askReason ? (
              <>
                <label
                  htmlFor={changeReasonId}
                  className="text-xs"
                  style={{ color: "var(--color-text-3)" }}
                >
                  {KPI_COPY.cell.changeReason}
                </label>
                <input
                  id={changeReasonId}
                  data-testid="kpi-change-reason"
                  style={inp}
                  value={changeReason}
                  maxLength={500}
                  onChange={(e) => setChangeReason(e.target.value)}
                  placeholder={KPI_COPY.cell.changeReasonHint}
                />
              </>
            ) : null}
            {isClient ? (
              <KpiCellClientNote
                value={reply}
                onChange={setReply}
                label={clarifying ? KPI_COPY.portal.replyLabel : KPI_COPY.portal.noteLabel}
                placeholder={
                  clarifying ? KPI_COPY.portal.replyPlaceholder : KPI_COPY.portal.noteHint
                }
              />
            ) : null}
            <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
              Rapor dipnotu
            </span>
            <input
              aria-label="Rapor dipnotu"
              style={inp}
              value={footnote}
              onChange={(e) => setFootnote(e.target.value)}
              placeholder="(boş)"
            />
          </div>
          {bdTotal ? null : bdSection}
          {props.notes && evidence.entityId ? (
            <div className="mt-3">
              <KpiCellNotes
                audience={props.notes.audience}
                setId={props.notes.setId}
                cell={{
                  metricId: indicator.metricId,
                  entityId: evidence.entityId,
                  year: evidence.year,
                }}
                composer={!isClient}
                onAdd={props.onOpenNotes}
                onChanged={props.notes.onChanged}
              />
            </div>
          ) : null}
          {error ? (
            <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
              {error}
            </div>
          ) : null}
          {warning ? (
            <div
              role="status"
              className="text-xs mt-2"
              style={{ color: "var(--color-warning-text)" }}
            >
              {warning} — değer sayı olarak {"("}birim soneki atılarak{")"} kaydedilir; birimi
              çevirmek için göstergenin birimine göre yazın.
            </div>
          ) : null}
          {props.formLink && !isClient ? (
            <KpiCellEditorFormLink
              mismatch={props.formLink}
              onApplied={() => {
                evidence.onChanged();
                onCancel();
              }}
            />
          ) : null}
          <KpiCellEditorActions
            canClientApprove={canClientApprove}
            approving={approving}
            onApprove={() => void approveForCompany()}
            onCancel={onCancel}
            onSubmit={submit}
            warning={!!warning}
          />
        </>
      ) : tab === "evidence" ? (
        <>
          <EvidencePanel
            clientId={evidence.clientId}
            valueId={cell?.valueId}
            indicator={indicator}
            indicators={evidence.indicators}
            years={evidence.years}
            entityId={evidence.entityId}
            year={evidence.year}
            readOnly={evidence.readOnly}
            onChanged={evidence.onChanged}
            portalSetId={evidence.portalSetId}
          />
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
            >
              {KPI_EVIDENCE_COPY.reported.close}
            </button>
          </div>
        </>
      ) : (
        <KpiCellHistory
          cell={cell}
          indicator={indicator}
          currentUserId={currentUserId}
          isClient={isClient}
          notes={
            props.notes && evidence.entityId
              ? {
                  setId: props.notes.setId,
                  cell: {
                    metricId: indicator.metricId,
                    entityId: evidence.entityId,
                    year: evidence.year,
                  },
                  onChanged: props.notes.onChanged,
                }
              : undefined
          }
          onClose={onCancel}
        />
      )}
    </div>,
    document.body,
  );
}
